import React, { SetStateAction, useContext, useEffect, useRef } from 'react';
import { Button, Flex, Typography } from 'antd';
import { useReactFlow } from '@xyflow/react';
import { CloseOutlined } from '@ant-design/icons';
import { NODES_TYPES } from '../types';
import EventParams from './drawer-node-params/EventParams';
import ActionParams from './drawer-node-params/ActionParams';
import ConditionParams from './drawer-node-params/ConditionParams';
import TargetParams from './drawer-node-params/TargetParams';
import QuestParams from './drawer-node-params/QuestParams';
import CollectibleParams from './drawer-node-params/CollectibleParams';
import DestroyableParams from './drawer-node-params/DestroyableParams';
import EventLinkNodeParams from './drawer-node-params/EventLinkNodeParams';
import { ThemeContext } from '../../../../../pages/_app';
import styles from '../timeline-node-manager.module.css';
const { Title } = Typography;

type SideDrawerProps = {
  isOpen: boolean;
  nodes: any[];
  setIsOpen: React.Dispatch<SetStateAction<boolean>>;
  selectedNode: any;
  selectableAssets: any[];
  setSelectedNode: React.Dispatch<SetStateAction<any>>;
};

const nameToTypesMap = {
  [NODES_TYPES.EVENT]: 'Event',
  [NODES_TYPES.EVENT_LINK]: 'Linking Events',
  [NODES_TYPES.CONDITION]: 'Condition',
  [NODES_TYPES.TARGET]: 'Target',
  [NODES_TYPES.ACTION]: 'Action',
  [NODES_TYPES.QUEST]: 'Quest',
  [NODES_TYPES.COLLECTIBLE]: 'Collectible',
  [NODES_TYPES.DESTROYABLE]: 'Destroyable',
};

export default function SideDrawer({
  nodes,
  isOpen = false,
  setIsOpen,
  selectedNode,
  selectableAssets,
  setSelectedNode,
}: SideDrawerProps) {
  const { setNodes } = useReactFlow();
  const nodesRef = useRef(nodes);
  const { isDark } = useContext(ThemeContext);
  const type = selectedNode?.type;
  const typeName = nameToTypesMap[type];

  const sideDrawerStyle = {
    backgroundColor: isDark ? 'rgba(19, 19, 19, 1)' : '#FFF',
  };

  function renderContentParams(id: string) {
    switch (type) {
      case NODES_TYPES.EVENT:
        return <EventParams nodeId={id} />;
      case NODES_TYPES.ACTION:
        return <ActionParams nodeId={id} selectableAssets={selectableAssets} />;
      case NODES_TYPES.CONDITION:
        return (
          <ConditionParams nodeId={id} selectableAssets={selectableAssets} />
        );
      case NODES_TYPES.TARGET:
        return <TargetParams nodeId={id} selectableAssets={selectableAssets} />;
      case NODES_TYPES.EVENT_LINK:
        return <EventLinkNodeParams nodeId={id} />;
      case NODES_TYPES.COLLECTIBLE:
        return (
          <CollectibleParams nodeId={id} selectableAssets={selectableAssets} />
        );
      case NODES_TYPES.DESTROYABLE:
        return (
          <DestroyableParams nodeId={id} selectableAssets={selectableAssets} />
        );
      case NODES_TYPES.QUEST:
        return <QuestParams nodeId={id} />;
      default:
        return null;
    }
  }

  async function onSaveActionPosition(e: any) {
    setSelectedNode(null);
    setIsOpen(false);

    const { nodeId, propertyKey, position } = e.detail;
    const node = nodesRef.current.find((node) => node.id === nodeId);

    if (!node) {
      return;
    }

    await setNodes((nodes) =>
      nodes.map((n: any) => {
        const keys = propertyKey.split('.');
        let objectToSet = { ...position };

        for (let i = keys.length - 1; i >= 0; i--) {
          objectToSet = {
            [keys[i]]: { ...objectToSet },
          };
        }

        return n.id === nodeId
          ? {
              ...n,
              data: {
                ...n.data,
                params: {
                  ...n.data.params,
                  ...objectToSet,
                },
              },
            }
          : n;
      })
    );

    setSelectedNode(node);
    setIsOpen(true);
  }

  useEffect(() => {
    window.addEventListener('update_action_position', onSaveActionPosition);

    window.dispatchEvent(new CustomEvent('timeline_loaded'));
    return () => {
      window.removeEventListener(
        'update_action_position',
        onSaveActionPosition
      );
    };
  }, []);

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  if (!isOpen || !selectedNode) {
    return null;
  }

  return (
    <div className={styles.sideDrawer} style={sideDrawerStyle}>
      <Flex
        justify='space-between'
        align='start'
        className={styles.headerSideDrawer}
      >
        <Title level={5}>Node Settings - {typeName}</Title>
        <Button onClick={() => setIsOpen(false)}>
          <CloseOutlined />
        </Button>
      </Flex>
      {renderContentParams(selectedNode?.id || '')}
    </div>
  );
}
